import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';

import { useVisitorTracking } from '../hooks/useVisitorTracking';
import NetworkErrorPage from '../pages/NetworkErrorPage';
import { adminService } from '../services/adminService';
import { errorMessage, normalizeApiError } from '../services/apiError';
import { authService } from '../services/authService';
import { tokenService } from '../services/tokenService';
import { flushPendingAuthAnalytics, setAnalyticsUserId, trackLogin, trackLogout } from '../utils/analytics';
import type { SystemRole } from '../utils/systemRoles';

export interface User {
  id: string;
  email: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  avatarUrl?: string;
  profileImageUrl?: string;
  emailVerified: boolean;
  isActive: boolean;
  lastLogin?: string;
  authProvider?: string;
  canChangePassword?: boolean;
  canChangeEmail?: boolean;
  system_role?: SystemRole;
  /** Roles on top of the system role (e.g. marketing); they open the marketing, operations and sales pages. */
  additional_roles?: string[];
  subscription_tier?: string;
  subscription_status?: string;
  credits_balance?: number;
  hasByokKeys?: boolean;
  disposableEmail?: boolean;
  country?: string;
  /** USD or INR chosen on the profile page; null follows the country. */
  preferred_currency?: string | null;
  stateCode?: string;
  gstNumber?: string;
  orgName?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string, country: string, stateCode?: string) => Promise<void>;
  logout: () => Promise<void>;
  getCurrentUser: () => Promise<void>;
  clearError: () => void;
  networkError: boolean;
  isImpersonating: boolean;
  originalAdminUser: User | null;
  loginAsUser: (userId: string) => Promise<void>;
  exitImpersonation: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const ADMIN_TOKEN_KEY = 'admin_token';
const ADMIN_REFRESH_KEY = 'admin_refresh_token';
const ADMIN_USER_KEY = 'admin_user';

const getErr = (err: unknown, msg: string): string => errorMessage(err, msg);

const saveTokens = (access: string, refresh?: string) => {
  tokenService.setTokens(access, refresh || tokenService.getRefreshToken() || '');
};

// eslint-disable-next-line max-statements
const onFetchErr = (
  err: unknown,
  setU: (u: User | null) => void,
  setE: (e: string | null) => void,
  setNE: (ne: boolean) => void
) => {
  const normalized = normalizeApiError(err);
  console.error('Failed to fetch current user:', normalized);

  if (normalized.kind === 'network' || normalized.kind === 'timeout') {
    setNE(true);
    return;
  }

  setE(normalized.message);
  if (normalized.status === 401 || normalized.code === 'AUTH_EMAIL_NOT_VERIFIED') {
    setU(null);
    tokenService.clearTokens();
  }
};

const initFlow = (setL: (l: boolean) => void, setE: (e: string | null) => void) => {
  setL(true);

  setE(null);
};

const noToken = (setU: (u: User | null) => void, setL: (l: boolean) => void) => {
  setU(null);

  setL(false);
};

const fetchUser = async (setU: (u: User | null) => void) => {
  setU(await authService.getCurrentUser());
};

const doLogin = async (email: string, password: string) => {
  const res = await authService.emailLogin(email, password);

  saveTokens(res.access_token, res.refresh_token);
};

const doRegister = async (email: string, password: string, displayName: string, country: string, stateCode?: string) => {
  await authService.emailRegister(email, password, displayName, country, stateCode);
  // Do NOT auto-login — user must verify email first
};

const tryLogout = async () => {
  try {
    await authService.logout();
  } catch (err) {
    console.warn('Logout API call failed, clearing tokens locally:', err);
  }
};

const clearSess = (setU: (u: User | null) => void) => {
  setU(null);

  tokenService.clearTokens();
};

const handleAsync = async (
  fn: () => Promise<void>,
  setL: (l: boolean) => void,
  setE: (e: string | null) => void,
  errMsg: string
) => {
  try {
    await fn();
  } catch (err) {
    setE(getErr(err, errMsg));

    throw err;
  } finally {
    setL(false);
  }
};

const getUserFlow = async (
  setU: (u: User | null) => void,
  setL: (l: boolean) => void,
  setE: (e: string | null) => void,
  setNE: (ne: boolean) => void
) => handleAsync(
  async () => {
    try {
      await fetchUser(setU);
    } catch (err) {
      onFetchErr(err, setU, setE, setNE);

      throw err;
    }
  },
  setL,
  setE,
  'We couldn’t load your account. Refresh and try again.'
);

const loginFlow = async (
  email: string,
  password: string,
  setL: (l: boolean) => void,
  setE: (e: string | null) => void,
  refetch: () => Promise<void>
) => handleAsync(
  async () => {
    await doLogin(email, password);
    trackLogin('email');

    await refetch();
  },
  setL,
  setE,
  'Login failed'
);

const registerFlow = async (
  email: string,
  password: string,
  displayName: string,
  country: string,
  stateCode: string | undefined,
  setL: (l: boolean) => void,
  setE: (e: string | null) => void,
  _refetch: () => Promise<void>
) => handleAsync(
  async () => {
    await doRegister(email, password, displayName, country, stateCode);
    // Do NOT refetch user — no tokens issued until email is verified
  },
  setL,
  setE,
  'Registration failed'
);

const logoutFlow = async (
  setU: (u: User | null) => void,
  setL: (l: boolean) => void,
  setE: (e: string | null) => void
) => handleAsync(
  async () => {
    await tryLogout();
    trackLogout();

    clearSess(setU);
  },
  setL,
  setE,
  'Logout failed'
);

const useGetUser = (
  setU: (u: User | null) => void,
  setL: (l: boolean) => void,
  setE: (e: string | null) => void,
  setNE: (ne: boolean) => void
) => useCallback(async () => {
  initFlow(setL, setE);

  if (!tokenService.getAccessToken()) {
    noToken(setU, setL);

    return;
  }

  await getUserFlow(setU, setL, setE, setNE);
}, [setU, setL, setE, setNE]);

const useLoginFn = (
  setL: (l: boolean) => void,
  setE: (e: string | null) => void,
  refetch: () => Promise<void>
) => useCallback(
  async (email: string, password: string) => {
    initFlow(setL, setE);

    await loginFlow(email, password, setL, setE, refetch);
  },
  [setL, setE, refetch]
);

const useRegisterFn = (
  setL: (l: boolean) => void,
  setE: (e: string | null) => void,
  refetch: () => Promise<void>
) => useCallback(
  async (email: string, password: string, displayName: string, country: string, stateCode?: string) => {
    initFlow(setL, setE);

    await registerFlow(email, password, displayName, country, stateCode, setL, setE, refetch);
  },
  [setL, setE, refetch]
);

const useLogoutFn = (
  setU: (u: User | null) => void,
  setL: (l: boolean) => void,
  setE: (e: string | null) => void
) => useCallback(async () => {
  initFlow(setL, setE);

  await logoutFlow(setU, setL, setE);
}, [setU, setL, setE]);

const useAuthHandlers = (
  setUser: (u: User | null) => void,
  setIsLoading: (l: boolean) => void,
  setError: (e: string | null) => void,
  setNetworkError: (ne: boolean) => void
) => {
  const getCurrentUser = useGetUser(setUser, setIsLoading, setError, setNetworkError);

  const login = useLoginFn(setIsLoading, setError, getCurrentUser);

  const register = useRegisterFn(setIsLoading, setError, getCurrentUser);

  const logout = useLogoutFn(setUser, setIsLoading, setError);

  return { getCurrentUser, login, register, logout, clearError: () => setError(null) };
};

const useAuthState = () => {
  const [user, setUser] = useState<User | null>(null);

  /* Only a visitor who actually has a stored token has an auth state left to
     resolve. Starting `true` unconditionally made every anonymous page load
     commit one "still loading" frame before settling on the identical
     anonymous UI — which is what makes the prerendered landing page flicker on
     reload (the header fades out and back in, and the chat card reserves and
     then releases the action-bar row, shifting everything below it). Reading
     the token synchronously lets anonymous visitors render their final state
     on the very first paint. */
  const [isLoading, setIsLoading] = useState(() => !!tokenService.getAccessToken());

  const [error, setError] = useState<string | null>(null);

  const [networkError, setNetworkError] = useState(false);

  return { user, setUser, isLoading, setIsLoading, error, setError, networkError, setNetworkError };
};

/**
 * getCurrentUser updates AuthContext state before rejecting. Background callers
 * must still observe that rejection so it cannot become a global
 * `unhandledrejection`.
 */
const refreshCurrentUserInBackground = (getCurrentUser: () => Promise<void>) => {
  void getCurrentUser().catch(() => undefined);
};

const useInitializeAuth = (getCurrentUser: () => Promise<void>, setIsLoading: (l: boolean) => void) => {
  useEffect(() => {
    if (tokenService.getAccessToken()) {
      refreshCurrentUserInBackground(getCurrentUser);
    } else {
      setIsLoading(false);
    }
  }, [getCurrentUser, setIsLoading]);
};

/**
 * Detect OAuth tokens set by external flows (mobile OAuth sheets, other tabs).
 *
 * On mobile browsers, OAuth often completes in a separate browsing context
 * (Safari auth sheet, Chrome Custom Tab). When tokens are written to
 * localStorage in that context, the original page needs to detect them:
 *
 * 1. `storage` event — fires when another tab/window modifies localStorage
 * 2. `visibilitychange` — fires when page becomes visible after OAuth sheet closes
 * 3. `pageshow` — fires on bfcache restoration (back/forward navigation)
 */
const useOAuthDetection = (
  user: User | null,
  getCurrentUser: () => Promise<void>
) => {
  useEffect(() => {
    // When another tab/sheet writes tokens to localStorage
    // eslint-disable-next-line no-undef
    const onStorage = (e: StorageEvent) => {
      if (e.key === (process.env.REACT_APP_TOKEN_STORAGE_KEY || 'auth_token') && e.newValue && !user) {
        refreshCurrentUserInBackground(getCurrentUser);
      }
    };

    // When page becomes visible (OAuth sheet closed, tab switched back)
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && tokenService.hasToken() && !user) {
        refreshCurrentUserInBackground(getCurrentUser);
      }
    };

    // On bfcache restoration (iOS Safari aggressive caching)
    // eslint-disable-next-line no-undef
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted && tokenService.hasToken() && !user) {
        refreshCurrentUserInBackground(getCurrentUser);
      }
    };

    window.addEventListener('storage', onStorage);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', onPageShow);

    return () => {
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [user, getCurrentUser]);
};

// ── Impersonation Hooks ─────────────────────────────────────────────

const saveAdminSession = (user: User) => {
  localStorage.setItem(ADMIN_TOKEN_KEY, tokenService.getAccessToken() || '');
  localStorage.setItem(ADMIN_REFRESH_KEY, tokenService.getRefreshToken() || '');
  localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(user));
};

const clearAdminSession = () => {
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  localStorage.removeItem(ADMIN_REFRESH_KEY);
  localStorage.removeItem(ADMIN_USER_KEY);
};

const getStoredAdminUser = (): User | null => {
  const raw = localStorage.getItem(ADMIN_USER_KEY);

  if (!raw) return null;

  try { return JSON.parse(raw); } catch { return null; }
};

const useImpersonationState = () => {
  const [originalAdminUser, setOriginalAdminUser] = useState<User | null>(getStoredAdminUser);

  const isImpersonating = !!originalAdminUser;

  return { originalAdminUser, setOriginalAdminUser, isImpersonating };
};

const useImpersonationHandlers = (
  currentUser: User | null,
  setUser: (u: User | null) => void,
  setOriginalAdminUser: (u: User | null) => void,
  getCurrentUser: () => Promise<void>,
) => {
  const loginAsUser = useCallback(async (userId: string) => {
    if (!currentUser) return;

    // Save current admin session
    saveAdminSession(currentUser);

    // Get impersonated user tokens
    const resp = await adminService.loginAsUser(userId);

    // Switch to impersonated user tokens
    tokenService.setTokens(resp.access_token, resp.refresh_token);
    setOriginalAdminUser(currentUser);

    // Fetch the impersonated user profile
    await getCurrentUser();
  }, [currentUser, setOriginalAdminUser, getCurrentUser]);

  const exitImpersonation = useCallback(() => {
    const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
    const adminRefresh = localStorage.getItem(ADMIN_REFRESH_KEY);
    const adminUser = getStoredAdminUser();

    if (adminToken && adminRefresh) {
      tokenService.setTokens(adminToken, adminRefresh);
    }

    clearAdminSession();
    setOriginalAdminUser(null);

    if (adminUser) {
      setUser(adminUser);
    }
  }, [setUser, setOriginalAdminUser]);

  return { loginAsUser, exitImpersonation };
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, setUser, isLoading, setIsLoading, error, setError, networkError, setNetworkError } = useAuthState();

  const handlers = useAuthHandlers(setUser, setIsLoading, setError, setNetworkError);

  const { originalAdminUser, setOriginalAdminUser, isImpersonating } = useImpersonationState();

  const { loginAsUser, exitImpersonation } = useImpersonationHandlers(
    user, setUser, setOriginalAdminUser, handlers.getCurrentUser,
  );

  useInitializeAuth(handlers.getCurrentUser, setIsLoading);

  useOAuthDetection(user, handlers.getCurrentUser);

  useVisitorTracking(user?.id, isImpersonating);

  useEffect(() => {
    setAnalyticsUserId(isImpersonating ? undefined : user?.id);
    if (user && !isImpersonating) flushPendingAuthAnalytics();
  }, [user, isImpersonating]);

  const value: AuthContextType = {
    user, isLoading, isAuthenticated: !!user, error, networkError,
    isImpersonating, originalAdminUser, loginAsUser, exitImpersonation,
    ...handlers,
  };

  return (
    <AuthContext.Provider value={value}>
      {networkError ? <NetworkErrorPage /> : children}
    </AuthContext.Provider>
  );
};

/** The auth state when an AuthProvider is above, else undefined (for optional, auth-aware UI). */
export const useOptionalAuth = (): AuthContextType | undefined => useContext(AuthContext);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};
